/** @format */

import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, IconButton, Typography } from '@mui/material';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery } from '@tanstack/react-query';
import { getPending, reviewKeys } from './queries';

const DISMISS_KEY = 'accessReviewBannerDismissed';

const readDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
};

/** slim reminder above every page while the signed-in user has open access reviews */
export default function ReviewBanner() {
  const location = useLocation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(readDismissed);

  const pending = useQuery({
    queryKey: reviewKeys.pending,
    queryFn: getPending,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    enabled: !dismissed,
    // a reminder is optional: any failure stays silent
    throwOnError: false,
    meta: { silent: true },
  });

  const count = pending.isError ? 0 : pending.data ?? 0;
  if (dismissed || count <= 0 || location.pathname.startsWith('/access-reviews')) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* private mode: hide for this page only */
    }
  };

  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: { xs: 1.5, sm: 2.5 },
        py: 0.75,
        bgcolor: '#EAF1FF',
        borderBottom: '1px solid #C9DAFB',
        color: '#12467A',
      }}
    >
      <FactCheckOutlinedIcon sx={{ fontSize: 18 }} />
      <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 500 }}>
        You have {count} access review{count === 1 ? '' : 's'} to complete.
      </Typography>
      <Button size="small" variant="contained" onClick={() => navigate('/access-reviews')} sx={{ py: 0.25, textTransform: 'none' }}>
        Open
      </Button>
      <IconButton size="small" aria-label="Hide reminder" onClick={dismiss} sx={{ color: 'inherit' }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
